/**
 * Step-up (re-verification) with a typed outcome (W1 / F40).
 *
 * THE DEFECT
 * ----------
 * Clerk's `useReverification` REJECTS when the step-up is cancelled, which is
 * indistinguishable at the call site from the operation genuinely failing. Four
 * sites wrap it; three treat a cancellation as an error, so a user who simply
 * dismisses the modal gets a red toast and a Sentry event for something they
 * chose to do.
 *
 * business-web is the clearest case: it stores the real reason in a ref and
 * reads it on cancellation — but a plain dismissal leaves that ref `null`, so
 * `new Error(String(null))` reports the literal string **"null"** to Sentry. A
 * meaningless error, filed against a non-event.
 *
 * THE FIX
 * -------
 * Return a discriminated union instead of throwing, so no call site can forget
 * the cancelled case — the compiler will not let it. Cancellation is `idle`:
 * no Sentry event, no error copy, and deliberately NO NEW i18n KEY, because
 * there is nothing to tell the user about an action they cancelled themselves.
 *
 *   const stepUp = useStepUp();
 *   const result = await stepUp(() => doTheSensitiveThing());
 *   if (result.outcome === 'cancelled') return;      // silence is correct
 *   if (result.outcome === 'failed') showError(result.error);
 *   // result.outcome === 'completed' → result.value
 *
 * WHY @tonnie/sdk
 * ---------------
 * It is the package both portals already import, and the only one that already
 * USES Clerk. Note it did not DECLARE it: `packages/sdk/package.json` carried no
 * `dependencies`, `peerDependencies` or `devDependencies` at all, and
 * `providers/AuthProvider.tsx` imported `@clerk/clerk-react` resolved purely by
 * hoisting from the root. That is the same undeclared-transitive shape as the
 * pruned `undici` that once broke a production boot, so the dependency is now
 * declared alongside this hook.
 */

import { useCallback } from 'react';
import { useReverification } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';

/** What happened. Exhaustive on purpose — see the union below. */
export type StepUpOutcome<T> =
  | { outcome: 'completed'; value: T }
  /** The user dismissed the prompt. Not an error. Report nothing. */
  | { outcome: 'cancelled' }
  /** The operation itself failed. This one is worth surfacing. */
  | { outcome: 'failed'; error: Error };

/**
 * Is this the rejection Clerk raises when a step-up is cancelled?
 *
 * Delegates to Clerk's own `isReverificationCancelledError` — the library
 * exports a guard for exactly this, and hand-rolling string matching against
 * someone else's error shape is how a detection silently stops working at the
 * next minor bump.
 *
 * The shape fallback exists only for the case where that import is unavailable
 * (an older Clerk, or a test double), and it is deliberately loose: a
 * cancellation misread as a failure produces a spurious error report, which is
 * the whole defect this hook exists to stop.
 */
export function isStepUpCancelled(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  try {
    if (isReverificationCancelledError(error as never)) return true;
  } catch {
    // Guard unavailable — fall through to the shape check.
  }
  const candidate = error as { code?: unknown; message?: unknown; name?: unknown };
  const code = typeof candidate.code === 'string' ? candidate.code.toLowerCase() : '';
  const message = typeof candidate.message === 'string' ? candidate.message.toLowerCase() : '';
  const name = typeof candidate.name === 'string' ? candidate.name.toLowerCase() : '';
  return (
    code.includes('cancelled') ||
    code.includes('canceled') ||
    name.includes('reverificationcancelled') ||
    message.includes('reverification was cancelled') ||
    message.includes('reverification was canceled')
  );
}

/**
 * Wrap a sensitive operation in Clerk's step-up flow.
 *
 * The returned function never throws: every path produces a `StepUpOutcome`.
 * That is the whole point — a throwing API is what let three of four call sites
 * treat "the user changed their mind" as a defect.
 */
export function useStepUp() {
  // `useReverification` must wrap a function at hook level, so the generic
  // operation is passed through and invoked inside.
  const withReverification = useReverification(async (operation: () => Promise<unknown>) =>
    operation()
  );

  return useCallback(
    async <T>(operation: () => Promise<T>): Promise<StepUpOutcome<T>> => {
      try {
        const value = (await withReverification(operation as () => Promise<unknown>)) as T;
        return { outcome: 'completed', value };
      } catch (error) {
        if (isStepUpCancelled(error)) return { outcome: 'cancelled' };
        return {
          outcome: 'failed',
          // Never `new Error(String(error))` on a null: that is how "null"
          // reached Sentry as an error message in the first place.
          error:
            error instanceof Error
              ? error
              : new Error(
                  typeof error === 'string' && error ? error : 'Step-up verification failed'
                ),
        };
      }
    },
    [withReverification]
  );
}
